import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils/cn";
import { apiErrorMessage } from "@/lib/http";
import { maskCnpj, maskPhone, isValidCnpj, isValidPhone } from "@/lib/format/masks";
import { register } from "../api";
import { SocialButtons } from "../components/SocialButtons";
import { PasswordFields } from "../components/PasswordFields";

const inputClass =
  "w-full rounded-xl border border-border bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RegisterPage() {
  const navigate = useNavigate();
  const [agencyName, setAgencyName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!agencyName.trim() || !contactName.trim()) {
      toast.error("Preencha o nome da imobiliária e do responsável.");
      return;
    }
    if (!isValidCnpj(cnpj)) {
      toast.error("CNPJ inválido.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error("E-mail inválido.");
      return;
    }
    if (!isValidPhone(phone)) {
      toast.error("Telefone inválido.");
      return;
    }
    if (password.length < 8) {
      toast.error("A senha deve ter ao menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não coincidem.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await register({
        agencyName: agencyName.trim(),
        cnpj,
        contactName: contactName.trim(),
        email: email.trim(),
        phone,
        password,
      });
      navigate("/pending", { replace: true, state: result });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Não foi possível concluir o cadastro."));
      setSubmitting(false);
    }
  }

  return (
    <Card className="p-8 shadow-card border-border/60">
      <div className="mb-6 flex flex-col items-center gap-3 text-center">
        <Logo />
        <div>
          <h1 className="text-lg font-bold tracking-tight text-ink">Criar conta da imobiliária</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Cadastre sua imobiliária. Após a análise da nossa equipe, o acesso é liberado.
          </p>
        </div>
      </div>

      <SocialButtons />

      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-border/60" />
        <span className="text-xs text-ink-soft">ou com e-mail</span>
        <span className="h-px flex-1 bg-border/60" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-ink-muted">Nome da imobiliária</label>
          <input
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            required
            placeholder="Imobiliária Exemplo"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-muted">CNPJ</label>
            <input
              value={cnpj}
              onChange={(e) => setCnpj(maskCnpj(e.target.value))}
              required
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-muted">Telefone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(maskPhone(e.target.value))}
              required
              inputMode="tel"
              placeholder="(00) 00000-0000"
              className={inputClass}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-ink-muted">Nome do responsável</label>
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-ink-muted">E-mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="contato@imobiliaria.com.br"
            className={inputClass}
          />
        </div>

        <PasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
        />

        <button
          type="submit"
          disabled={submitting}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl bg-brand-grad py-3.5 text-sm font-bold text-white shadow-glow transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70",
            submitting && "cursor-not-allowed",
          )}
        >
          {submitting ? <IconLoader2 className="animate-spin" size={18} /> : "Criar conta"}
        </button>
      </form>

      <Link
        to="/login"
        className="mt-6 inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink"
      >
        <IconArrowLeft size={15} aria-hidden />
        Já tenho conta
      </Link>
    </Card>
  );
}
